import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { useEffect } from "react";
import { useExpectedTransactionTypes } from "@src/api/useExpectedTransactionTypes";
import Container from "@src/components/shared/Container";
import TitleAndDesc from "@src/components/shared/TitleAndDesc";
import { Button } from "@src/components/ui/button";
import { Checkbox } from "@src/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@src/components/ui/form";
import { Skeleton } from "@src/components/ui/skeleton";
import useZustandStore from "@src/helpers/zustand";
import { cn } from "@src/lib/utils";
import { DIGIT_SPACE_DASH_LATIN_GEORGIAN_ONLY_REGEX } from "@src/helpers/regex";
import { Textarea } from "@src/components/ui/textarea";
import { useKYCAddUserLog, LoggerTypes } from "@src/api/useKYCAddUserLog";

const FormSchema = z.object({
  expectedTransactionsOther: z.string().optional(),
  expectedTransactionTypes: z.array(z.number()),
});

export default function PurposeOfRelationship() {
  const addUserLogMutation = useKYCAddUserLog();
  const { t, i18n } = useTranslation();
  const { data: zData, setData } = useZustandStore((state) => state);
  const expectedTransactionTypes = useExpectedTransactionTypes();
  const navigate = useNavigate();
  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      expectedTransactionsOther: zData.expectedTransactionsOther,
      expectedTransactionTypes: zData.expectedTransactionTypes,
    },
  });

  useEffect(() => {
    addUserLogMutation.mutate({
      eventName: LoggerTypes.PurposeOfRelationship,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const expectedTransactionsOtherFromForm = form.watch(
    "expectedTransactionsOther"
  );
  const expectedTransactionTypesFromForm = form.watch(
    "expectedTransactionTypes"
  );

  function onSubmit(data: z.infer<typeof FormSchema>) {
    setData({
      ...zData,
      expectedTransactionsOther: data.expectedTransactionsOther || "",
      expectedTransactionTypes: data.expectedTransactionTypes,
    });
    navigate(`/${i18n.language}/pep`);
  }

  useEffect(() => {
    if (expectedTransactionTypes.data) {
      const defaultChecked = expectedTransactionTypes.data.data.expectedTransactionTypes.find(
        (el) => el.defaultChecked
      );
      if (defaultChecked && !expectedTransactionTypesFromForm.length) {
        form.setValue("expectedTransactionTypes", [defaultChecked.expectedTransactionsID]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expectedTransactionTypes.isSuccess]);

  return (
    <Container className="max-md:min-h-[90vh] max-md:bg-white">
      <Form {...form}>
        <div className="py-4">
          <TitleAndDesc title={t("relationshipWithBank.title")} />
        </div>
        <form className="space-y-8" onSubmit={form.handleSubmit(onSubmit)}>
          <div className="max-md:min-h-[580px]">
            <div className="rounded-lg border border-solid border-gray-200 bg-white gap-0">
              <FormField
                control={form.control}
                name="expectedTransactionTypes"
                render={() => (
                  <FormItem>
                    {expectedTransactionTypes.isLoading ? (
                      [1, 2, 3, 4, 5].map((el) => {
                        return (
                          <FormItem
                            className={cn(
                              "flex items-center justify-between gap-1 border-b border-solid border-gray-200",
                              "last:border-b-0 relative"
                            )}
                            key={el}
                          >
                            <FormLabel className="!mt-0 text-sm font-normal leading-6 text-gray-700 p-4 h-14 w-full">
                              <Skeleton className="h-5 w-[170px]" />
                            </FormLabel>
                            <FormControl className="!mt-0 right-4 absolute">
                              <Skeleton className="h-5 w-5 rounded-full" />
                            </FormControl>
                          </FormItem>
                        );
                      })
                    ) : (
                      <>
                        {expectedTransactionTypes.data?.data.expectedTransactionTypes.map(
                          (item) => (
                            <FormField
                              control={form.control}
                              key={item.expectedTransactionsID}
                              name="expectedTransactionTypes"
                              render={({ field }) => {
                                return (
                                  <FormItem
                                    className="flex items-center justify-between flex-row-reverse gap-1 border-b border-solid border-gray-200 pr-4 last:border-b-0 relative !mt-0 cursor-pointer"
                                    key={item.expectedTransactionsID}
                                  >
                                    <FormControl>
                                      <Checkbox
                                        checked={field.value.includes(
                                          item.expectedTransactionsID
                                        )}
                                        onCheckedChange={(checked) => {
                                          checked
                                            ? field.onChange([
                                                ...field.value,
                                                item.expectedTransactionsID,
                                              ])
                                            : field.onChange(
                                                field.value.filter(
                                                  (value) =>
                                                    value !==
                                                    item.expectedTransactionsID
                                                )
                                              );
                                        }}
                                      />
                                    </FormControl>
                                    <FormLabel className="!mt-0 text-sm font-normal leading-6 text-gray-700 p-4 min-h-14 w-full cursor-pointer">
                                      {item.expectedTransactionsCode}
                                    </FormLabel>
                                  </FormItem>
                                );
                              }}
                            />
                          )
                        )}
                      </>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            {expectedTransactionTypesFromForm.includes(16) ? (
              <FormField
                control={form.control}
                name="expectedTransactionsOther"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <FormControl>
                        <Textarea
                          {...field}
                          className="mt-4 max-h-10 bg-white"
                          onChange={(e) => {
                            const { target } = e;
                            if (
                              DIGIT_SPACE_DASH_LATIN_GEORGIAN_ONLY_REGEX.test(
                                target.value
                              ) ||
                              !target.value
                            ) {
                              field.onChange(e);
                            }
                          }}
                          placeholder={t(
                            "relationshipWithBank.otherPlaceholder"
                          )}
                        />
                      </FormControl>
                    </FormItem>
                  );
                }}
              />
            ) : null}
          </div>
          <div className="z-10 max-md:fixed max-md:bottom-0 max-md:left-0 max-md:right-0">
            <div className="rounded-6 w-full self-end bg-white py-8 max-md:px-4 ">
              <Button
                className="w-full"
                disabled={
                  !expectedTransactionsOtherFromForm?.length &&
                  !expectedTransactionTypesFromForm.length
                }
                type="submit"
              >
                {t("continue")}
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </Container>
  );
}
